import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Form, Formik, FormikProps } from "formik"
import {
  CreateSubscriptionPayload,
  GooglePlace,
  CreateSubscriptionResponse,
  SubscriptionDetails
} from "../../../../types"
import SidePanel from "../../side-panel"
import { engagementFormSchema } from "./engagement-form-schema"
import ManageEngagementBasic from "./sections/basic"
import ManageEngagementMisc from "./sections/misc"
import ManageEngagementSelectors from "./sections/selectors"
import {
  useAppSelector,
  useToast,
  useValidateEmailAndNMLS
} from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  convertGooglePlaceToAddressObj,
  convertSubscriptionDetailsToPayload,
  validateZipcodeForPayload,
  normalizePhone
} from "../../../utils"
import {
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useFetchLeadDetailsQuery
} from "../../../../redux/queries"

interface Props {
  brandId?: string
  data?: SubscriptionDetails
  open: boolean
  leadId?: string
  nonqmEnabled?: boolean
  onClose: () => void
  submitCallback?: (response: CreateSubscriptionResponse) => void
}

const ManageEngagementPanel: FC<Props> = ({
  brandId,
  data,
  open,
  leadId,
  nonqmEnabled,
  onClose,
  submitCallback
}) => {
  const userDetails = useAppSelector(selectUserDetails)
  const { pushErrorToast, pushSuccessToast } = useToast()

  const { validateSubscriptionEmail, validateSubscriptionPhone } =
    useValidateEmailAndNMLS()

  const selectedbrandId = useMemo(() => {
    return (
      userDetails?.org?.brands?.find(
        brand => brand.default_loid === userDetails?.id
      )?.id ??
      brandId ??
      userDetails?.brand_id ??
      ""
    )
  }, [
    brandId,
    userDetails?.brand_id,
    userDetails?.id,
    userDetails?.org?.brands
  ])

  const { data: leadDetails } = useFetchLeadDetailsQuery(leadId ?? "", {
    skip: !leadId
  })

  const [googlePlace, setGooglePlace] = useState<GooglePlace>()

  const validate = useCallback(
    async (payload: CreateSubscriptionPayload | SubscriptionDetails) => {
      const emailErrors = await validateSubscriptionEmail(
        payload,
        selectedbrandId,
        leadId
      )
      const phoneErrors = await validateSubscriptionPhone(
        payload,
        selectedbrandId,
        leadId
      )
      const zipcodeErrors = validateZipcodeForPayload(
        payload as CreateSubscriptionPayload,
        googlePlace
      )

      return { ...emailErrors, ...phoneErrors, ...zipcodeErrors }
    },
    [
      validateSubscriptionEmail,
      validateSubscriptionPhone,
      selectedbrandId,
      googlePlace,
      leadId
    ]
  )

  const [
    createSubscription,
    {
      data: createSubscriptionData,
      isSuccess: isSuccessCreate,
      isError: isErrorCreate,
      isLoading: isLoadingCreate,
      error: createSubscriptionError,
      reset: resetCreateSubscription
    }
  ] = useCreateSubscriptionMutation()
  const [
    updateSubscription,
    {
      data: updateSubscriptionData,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      isLoading: isLoadingUpdate,
      reset: resetUpdateSubscription
    }
  ] = useUpdateSubscriptionMutation()

  const formRef = useRef<FormikProps<CreateSubscriptionPayload>>(null)
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false)

  const isCreateMode = !data?.id
  const isLoading = isLoadingCreate || isLoadingUpdate

  const setFormField = (name: string, val: string | number | boolean) => {
    formRef.current?.setFieldValue(name, val)
    setHasUserMadeChanges(true)
  }

  const resetForm = useCallback(() => {
    resetCreateSubscription()
    resetUpdateSubscription()
    formRef.current?.resetForm()
    setHasUserMadeChanges(false)
  }, [resetCreateSubscription, resetUpdateSubscription])

  useEffect(() => {
    if (open) {
      setHasUserMadeChanges(false)
    }
  }, [open])

  const onSubmit = async () => {
    if (!formRef.current?.values || !formRef.current.isValid) return

    const values = {
      ...formRef.current.values,
      phone: normalizePhone(formRef.current.values.phone)
    }

    if (isCreateMode) {
      createSubscription(values)
    } else {
      updateSubscription({ id: data.id, ...values })
    }
  }
  const leadDetailsWithEmail = leadDetails?.details
    ? {
        ...leadDetails.details,
        first_name: leadDetails.first_name,
        last_name: leadDetails.last_name
      }
    : undefined

  const baseValues = convertSubscriptionDetailsToPayload(
    data
      ? data
      : leadDetails
      ? ({ email: leadDetails.email, phone: leadDetails.phone } as any)
      : undefined,
    userDetails?.id,
    leadDetailsWithEmail,
    isCreateMode
  )

  const INITIAL_VALUES: CreateSubscriptionPayload = isCreateMode
    ? {
        ...baseValues,
        ...(leadId ? { lead_id: leadId } : {}),
        loan_type: baseValues.loan_type || "conventional",
        loan_term: baseValues.loan_term || "30",
        property_type: baseValues.property_type || "single_family_home",
        residency_type: baseValues.residency_type || "primary_home"
      }
    : baseValues

  useEffect(() => {
    if (googlePlace) {
      const address = convertGooglePlaceToAddressObj(googlePlace)
      setFormField("city", address.city ?? "")
      setFormField("county", address.county ?? "")
      setFormField("state", address.state ?? "")
      setFormField("street_address", address.street_address ?? "")
      setFormField("zipcode", address.zipcode ?? "")
    }
  }, [googlePlace])

  const currentData = isCreateMode
    ? createSubscriptionData
    : updateSubscriptionData
  const currentStatus = currentData?.status
  const currentIsSuccess = isCreateMode ? isSuccessCreate : isSuccessUpdate
  const currentIsError = isCreateMode ? isErrorCreate : isErrorUpdate

  useEffect(() => {
    if (currentIsSuccess && currentStatus === "success") {
      onClose()
      pushSuccessToast(
        `Successfully ${isCreateMode ? "created" : "updated"} subscription`
      )
      resetForm()
      submitCallback?.(currentData!)
    } else if (currentIsError || currentStatus === "error") {
      let errorMessage: string | undefined
      if (isCreateMode && createSubscriptionError) {
        if ("message" in createSubscriptionError)
          errorMessage = createSubscriptionError.message
        else if ("data" in createSubscriptionError)
          errorMessage = String(createSubscriptionError.data)
      }
      pushErrorToast(
        <>
          <div>Failed to {isCreateMode ? "create" : "update"} subscription</div>
          {errorMessage && (
            <div className="text-stone-400 text-sm">{errorMessage}</div>
          )}
        </>
      )
      resetCreateSubscription()
      resetUpdateSubscription()
    }
  }, [
    currentIsSuccess,
    currentStatus,
    currentIsError,
    currentData,
    onClose,
    pushSuccessToast,
    pushErrorToast,
    isCreateMode,
    resetForm,
    submitCallback,
    createSubscriptionError,
    resetCreateSubscription,
    resetUpdateSubscription
  ])

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={engagementFormSchema}
      onSubmit={onSubmit}
      innerRef={formRef}
      validateOnBlur
      validateOnChange
      validate={validate}
      enableReinitialize
    >
      {({ values, isValid, dirty }) => {
        const isDirty = dirty || hasUserMadeChanges
        const requiresChanges = isCreateMode ? !leadId && !isDirty : !isDirty
        const isButtonDisabled = isLoading || !isValid || requiresChanges

        return (
          <SidePanel
            title={`${isCreateMode ? "Add new" : "Edit"} subscription`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: `${isCreateMode ? "Add" : "Update"} subscription`,
                type: "primary",
                onClick: onSubmit,
                disabled: isButtonDisabled
              }
            ]}
          >
            <Form>
              <ManageEngagementBasic
                values={values}
                loSearchParams={{ brand_id: selectedbrandId }}
                onlyAdmins={false}
                setFormField={setFormField}
                setGooglePlace={setGooglePlace}
                googlePlace={googlePlace}
              />
              <ManageEngagementSelectors
                values={values}
                setFormField={setFormField}
                nonqmEnabled={nonqmEnabled}
              />
              <ManageEngagementMisc
                values={values}
                setFormField={setFormField}
                hasSendRateUpdateEmails
                hasCustomFee
              />
            </Form>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageEngagementPanel
